import {
	useEnvContext,
	useGoogleAnalyticsContext,
	useWebStoreContext
} from "contexts";
import {
	IShopPurchaseOffer,
	IShopPurchaseOfferSingleItem,
	IShopSection
} from "contexts/WebStoreProvider/types";
import { FeaturesFlags } from "helpers";
import { useAppDispatch, useAppSelector } from "hooks";
import { IOffer } from "pages/StoreFront/lib/SingleItems";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { offersApi } from "redux/apis";
import {
	onClosePopUp,
	onCollapseSingleItemCategory,
	onSeeFullTutorialAgain,
	onSetTutorialSlideNumber
} from "redux/slices";
import { selectShopSectionsState } from "redux/slices/shop-sections";
import { Slide1, Slide2, Slide3, Slide4, Slide5, Slide6 } from "./lib";

export const Tutorial = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { isFeatureFlagEnabled } = useEnvContext();
	const { shopSections } = useAppSelector(selectShopSectionsState);
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const { authToken, userTracker, postUserMetadata } = useWebStoreContext();

	const totalSlidesNumber = useMemo(
		() =>
			!isFeatureFlagEnabled(FeaturesFlags.LOYALTY_SHOP_FTUE) /* ||
			userTracker?.metadata?.hasSeenLPTutorial === "true" */
				? 7
				: 8,
		[userTracker?.metadata?.hasSeenLPTutorial]
	);

	const { data: purchaseOffers } = offersApi.endpoints.getOffers.useQueryState({
		offerType: "singleItem",
		isAuthenticated: !!authToken?.value
	});

	const [slideNumber, setSlideNumber] = useState(1);
	const [filteredSections, setFilteredSections] = useState<IShopSection[]>();
	const [sectionOffers, setSectionOffers] =
		useState<Array<IShopPurchaseOffer[] & IShopPurchaseOfferSingleItem[]>>();

	const handlePopState = () => {
		dispatch(onClosePopUp());
		dispatch(onSetTutorialSlideNumber(0));
		if (slideNumber >= 3) {
			dispatch(onCollapseSingleItemCategory(true));
		}
	};

	const handlePostData = async () => {
		await postUserMetadata({ key: "hasSeenTutorial", value: "true" });
	};

	const handleSkip = () => {
		reportEvent("tutorial", {
			source: "skip",
			step_number: slideNumber
		});
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "tutorial",
			event_type: "shop_visual_update",
			action: "skip",
			source: "tutorial",
			section_name: slideNumber?.toString()
		});
		handlePostData();
		handlePopState();
	};

	useEffect(() => {
		const filteredSections = shopSections?.mainShopSections?.filter((data) =>
			purchaseOffers?.offers?.find(
				(of: IOffer) =>
					of.sectionName === data.name && of.sectionName !== "limited_deals"
			)
		);
		const sectionOffers = filteredSections.map((data) => {
			return purchaseOffers?.offers?.filter(
				(of: IOffer) => of.sectionName === data.name
			);
		});
		setFilteredSections(filteredSections);
		setSectionOffers(sectionOffers);
	}, [shopSections, purchaseOffers]);

	useEffect(() => {
		if (slideNumber === 5) {
			dispatch(onCollapseSingleItemCategory(true));
			handlePostData();
		}
		if (slideNumber === totalSlidesNumber) {
			handlePopState();
			if (totalSlidesNumber === 8) {
				dispatch(onSeeFullTutorialAgain(true));
				navigate("/loyalty-shop");
			}
			return;
		}
		reportEvent("tutorial", {
			source: "step_render",
			step_number: slideNumber
		});
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "tutorial",
			event_type: "shop_visual_update",
			action: "step_render",
			source: "tutorial",
			section_name: slideNumber?.toString()
		});

		window.addEventListener("popstate", handlePopState);

		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, [slideNumber]);

	useEffect(() => {
		if (location.pathname !== "/shop") {
			dispatch(onClosePopUp());
		}
	}, [location]);

	return (
		<>
			{slideNumber === 1 && (
				<Slide1
					onNext={() => setSlideNumber(slideNumber + 1)}
					onSkip={handleSkip}
					totalSlidesNumber={totalSlidesNumber}
				/>
			)}
			{slideNumber === 2 && (
				<Slide2
					onNext={() => setSlideNumber(slideNumber + 1)}
					onSkip={handleSkip}
					totalSlidesNumber={totalSlidesNumber}
				/>
			)}
			{slideNumber === 3 && (
				<Slide3
					onNext={() => setSlideNumber(slideNumber + 1)}
					sectionOffers={sectionOffers}
					filteredSections={filteredSections}
					onSkip={handleSkip}
					totalSlidesNumber={totalSlidesNumber}
				/>
			)}
			{slideNumber === 4 && (
				<Slide4
					onNext={() => {
						setSlideNumber(slideNumber + 1);
						dispatch(onSetTutorialSlideNumber(7));
					}}
					onSkip={handleSkip}
					totalSlidesNumber={totalSlidesNumber}
				/>
			)}
			{slideNumber === 5 && (
				<Slide5
					onNext={() =>
						setSlideNumber(slideNumber + (totalSlidesNumber === 7 ? 2 : 1))
					}
					onSkip={handleSkip}
					totalSlidesNumber={totalSlidesNumber}
				/>
			)}
			{slideNumber === 6 && (
				<Slide6
					onNext={() => setSlideNumber(slideNumber + 2)}
					onSkip={handleSkip}
				/>
			)}
		</>
	);
};
